import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Button, Space, Tag, Badge, Select, message, Tooltip, Typography, Empty, Spin, Progress } from 'antd';
import {
  DashboardOutlined,
  MailOutlined,
  AuditOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  SendOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
  StarOutlined,
  StarFilled,
  GlobalOutlined,
  ShopOutlined,
  CalendarOutlined,
  LinkOutlined,
  RocketOutlined,
  CopyOutlined,
  ExportOutlined,
  HomeOutlined,
  RobotOutlined,
  VideoCameraOutlined,
  FireOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { loiAPI, scenarioSiteAdminAPI, couponRequestAPI } from '../services/api';

const { Text } = Typography;

const DEFAULT_CAMPAIGN_KEY = 'spo_default_campaign_id';

const SITE_TYPE_CONFIG = {
  scenario_site: { label: '行銷網站', color: '#1890ff', icon: <GlobalOutlined /> },
  marketing_site: { label: '舊版網站', color: '#722ed1', icon: <GlobalOutlined /> },
  partner_portal: { label: 'Partner Portal', color: '#D4A03C', icon: <TeamOutlined /> },
};

const BASE_URL = window.location.origin;

function HomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [recentDealers, setRecentDealers] = useState([]);
  const [publishedSites, setPublishedSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [houseStats, setHouseStats] = useState(null);
  const [aeoStats, setAeoStats] = useState(null);

  useEffect(() => {
    const houseApiBase = (() => {
      const h = window.location.hostname, p = window.location.port;
      if (!p || p === '80' || p === '443') return `${window.location.protocol}//${h}`;
      if (p === '3000') return `http://${h}:8000`;
      return `http://${h}:${p}`;
    })();
    fetch(`${houseApiBase}/h/life-property/homepage-stats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHouseStats(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loiAPI.getCampaigns().then((res) => {
      const list = res.data || [];
      setCampaigns(list);
      if (list.length > 0) {
        const saved = localStorage.getItem(DEFAULT_CAMPAIGN_KEY);
        const savedId = saved ? Number(saved) : null;
        const match = list.find((c) => c.id === savedId);
        setSelectedCampaignId(match ? match.id : list[0].id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    scenarioSiteAdminAPI.getPublishedSites().then(res => {
      setPublishedSites(res.data || []);
    }).catch(() => {}).finally(() => setSitesLoading(false));
  }, []);

  const loadStats = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const [statsRes, dealersRes] = await Promise.all([
        loiAPI.getDashboardStats(cid).catch(() => null),
        loiAPI.getDealerStatus(cid).catch(() => null),
      ]);
      if (statsRes) setStats(statsRes.data || statsRes);
      else setStats(null);
      if (dealersRes) {
        const d = dealersRes.data || dealersRes;
        const responded = (Array.isArray(d) ? d : [])
          .filter((x) => x.status === '已回覆意願' && x.responded_at)
          .sort((a, b) => new Date(b.responded_at) - new Date(a.responded_at))
          .slice(0, 5);
        setRecentDealers(responded);
      } else {
        setRecentDealers([]);
      }
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    loadStats(selectedCampaignId);
  }, [selectedCampaignId, loadStats]);

  const handleSetDefault = (cid) => {
    localStorage.setItem(DEFAULT_CAMPAIGN_KEY, String(cid));
    setSelectedCampaignId(cid);
    message.success('已設為預設活動');
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(`${BASE_URL}${url}`).then(() => {
      message.success('已複製連結');
    });
  };

  const defaultCampaignId = Number(localStorage.getItem(DEFAULT_CAMPAIGN_KEY)) || null;

  useEffect(() => {
    (async () => {
      try {
        const res = await couponRequestAPI.myStats();
        setAeoStats(res.data || null);
      } catch (e) {
        console.error('load aeo stats failed', e);
      }
    })();
  }, []);

  const statCards = [
    {
      title: '總經銷商',
      value: stats?.total_dealers ?? 0,
      icon: <TeamOutlined />,
      color: '#1890ff',
      bg: 'linear-gradient(135deg, #1890ff, #36cfc9)',
    },
    {
      title: '已發送',
      value: stats?.total_sent ?? 0,
      icon: <SendOutlined />,
      color: '#722ed1',
      bg: 'linear-gradient(135deg, #722ed1, #b37feb)',
    },
    {
      title: '已開啟',
      value: stats?.total_opened ?? 0,
      icon: <EyeOutlined />,
      color: '#faad14',
      bg: 'linear-gradient(135deg, #fa8c16, #ffc53d)',
    },
    {
      title: '已回覆',
      value: stats?.total_responded ?? 0,
      icon: <CheckCircleOutlined />,
      color: '#52c41a',
      bg: 'linear-gradient(135deg, #52c41a, #95de64)',
    },
  ];

  const quickActions = [
    {
      title: '意向書管理',
      desc: '查看經銷商回覆狀態、漏斗分析、品牌意願分佈',
      icon: <DashboardOutlined style={{ fontSize: 32 }} />,
      color: '#1890ff',
      bg: 'linear-gradient(135deg, #e6f7ff, #bae7ff)',
      path: '/loi',
    },
    {
      title: '數據匯入',
      desc: '匯入經銷商名單、產品清單 Excel 檔案',
      icon: <SettingOutlined style={{ fontSize: 32 }} />,
      color: '#13c2c2',
      bg: 'linear-gradient(135deg, #e6fffb, #b5f5ec)',
      path: '/loi/manage',
    },
    {
      title: '招標管理',
      desc: '招標通知發送、領標確認追蹤、報價確認管理',
      icon: <AuditOutlined style={{ fontSize: 32 }} />,
      color: '#722ed1',
      bg: 'linear-gradient(135deg, #f9f0ff, #d3adf7)',
      path: '/tender',
    },
    {
      title: '信件發送',
      desc: '自訂郵件內容、範本變數、批次發送管理',
      icon: <MailOutlined style={{ fontSize: 32 }} />,
      color: '#eb2f96',
      bg: 'linear-gradient(135deg, #fff0f6, #ffadd2)',
      path: '/email-compose',
    },
  ];

  return (
    <div>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #001529 0%, #003a8c 50%, #0050b3 100%)',
        borderRadius: 16,
        padding: '40px 48px',
        marginBottom: 24,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 200, height: 200,
          borderRadius: '50%', background: 'rgba(24,144,255,0.15)',
        }} />
        <div style={{
          position: 'absolute', bottom: -60, right: 120, width: 150, height: 150,
          borderRadius: '50%', background: 'rgba(54,207,201,0.1)',
        }} />

        <Row align="middle" gutter={24}>
          <Col flex="auto">
            <h1 style={{
              color: '#fff', fontSize: 28, margin: 0, fontWeight: 700,
            }}>
              marcom AI 行銷平台
            </h1>
            <p style={{
              color: 'rgba(255,255,255,0.75)', fontSize: 16, margin: '8px 0 0',
              maxWidth: 480,
            }}>
              數發局 115 年第一次電腦軟體共同供應契約採購案（案號 1150201）
              <br />意向書調查 &middot; 招標管理 &middot; 郵件通知
            </p>
            {campaigns.length > 0 && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Select
                  value={selectedCampaignId}
                  onChange={(val) => setSelectedCampaignId(val)}
                  style={{ minWidth: 360 }}
                  dropdownStyle={{ maxWidth: 500 }}
                  popupMatchSelectWidth={false}
                >
                  {campaigns.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      <FileTextOutlined style={{ marginRight: 6 }} />
                      {c.case_number ? `[${c.case_number}] ` : ''}{c.title}
                      {Number(localStorage.getItem(DEFAULT_CAMPAIGN_KEY)) === c.id && (
                        <Tag color="gold" style={{ marginLeft: 8, fontSize: 11 }}>預設</Tag>
                      )}
                    </Select.Option>
                  ))}
                </Select>
                <Tooltip title={selectedCampaignId === defaultCampaignId ? '目前為預設活動' : '設為預設活動'}>
                  <Button
                    type={selectedCampaignId === defaultCampaignId ? 'primary' : 'default'}
                    icon={selectedCampaignId === defaultCampaignId ? <StarFilled /> : <StarOutlined />}
                    onClick={() => handleSetDefault(selectedCampaignId)}
                    style={{
                      background: selectedCampaignId === defaultCampaignId ? '#faad14' : 'rgba(255,255,255,0.15)',
                      border: 'none',
                      color: '#fff',
                    }}
                  >
                    {selectedCampaignId === defaultCampaignId ? '預設' : '設為預設'}
                  </Button>
                </Tooltip>
              </div>
            )}
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<DashboardOutlined />}
                onClick={() => navigate('/loi')}
                style={{
                  background: 'linear-gradient(135deg, #1890ff, #36cfc9)',
                  border: 'none',
                  fontWeight: 600,
                  height: 44,
                  borderRadius: 8,
                }}
              >
                進入管理面板
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* ═══ HOUSE AI Showcase ═══ */}
      {houseStats && (
        <div style={{ marginBottom: 32 }}>
          <Card
            style={{
              borderRadius: 20, border: 'none', overflow: 'hidden',
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
              boxShadow: '0 8px 32px rgba(26,26,46,0.25)',
            }}
            styles={{ body: { padding: 0 } }}
          >
            <div style={{ position: 'relative' }}>
              {/* Decorative elements */}
              <div style={{ position: 'absolute', top: -50, right: -30, width: 200, height: 200, borderRadius: '50%', background: 'rgba(232,54,58,0.08)' }} />
              <div style={{ position: 'absolute', bottom: -40, left: 120, width: 160, height: 160, borderRadius: '50%', background: 'rgba(212,165,55,0.06)' }} />

              <div style={{ padding: '36px 40px 20px', position: 'relative' }}>
                <Row gutter={[32, 24]} align="middle">
                  <Col xs={24} lg={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 14,
                        background: 'linear-gradient(135deg, #e8363a, #d4a537)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(232,54,58,0.3)',
                      }}>
                        <HomeOutlined style={{ fontSize: 26, color: '#fff' }} />
                      </div>
                      <div>
                        <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: 1 }}>
                          HOUSE AI
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, margin: 0, letterSpacing: 0.5 }}>
                          AI-Powered Real Estate Platform
                        </p>
                      </div>
                      <Tag style={{
                        background: 'rgba(82,196,26,0.15)', border: '1px solid rgba(82,196,26,0.3)',
                        color: '#52c41a', borderRadius: 20, fontWeight: 600, fontSize: 11, marginLeft: 4,
                      }}>LIVE</Tag>
                    </div>

                    <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.8, margin: '0 0 20px', maxWidth: 500 }}>
                      AI 驅動的全自動不動產平台 — 自動找客源、AI 即時回覆、虛擬導覽、
                      智能議價、合約管理、售後關懷，零人力介入的智慧房仲服務。
                    </p>

                    {/* Mini stats */}
                    <Row gutter={[16, 12]} style={{ marginBottom: 20 }}>
                      {[
                        { icon: <HomeOutlined />, value: houseStats.total_properties, label: '物件', color: '#1890ff' },
                        { icon: <TeamOutlined />, value: houseStats.total_leads, label: '客戶', color: '#722ed1' },
                        { icon: <VideoCameraOutlined />, value: houseStats.total_tours, label: '導覽', color: '#d4a537' },
                        { icon: <EyeOutlined />, value: houseStats.total_views, label: '瀏覽', color: '#13c2c2' },
                      ].map((s, i) => (
                        <Col xs={12} sm={6} key={i}>
                          <div style={{
                            background: 'rgba(255,255,255,0.06)', borderRadius: 12,
                            padding: '12px 16px', border: '1px solid rgba(255,255,255,0.08)',
                            textAlign: 'center',
                          }}>
                            <div style={{ color: s.color, fontSize: 16, marginBottom: 2 }}>{s.icon}</div>
                            <div style={{ color: '#fff', fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{s.value}</div>
                            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{s.label}</div>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    <Space wrap>
                      <Button
                        type="primary" size="large"
                        icon={<ThunderboltOutlined />}
                        onClick={() => window.open('/h/life-property', '_blank')}
                        style={{
                          background: 'linear-gradient(135deg, #e8363a, #c02a2e)',
                          border: 'none', fontWeight: 700, height: 44, borderRadius: 10,
                          boxShadow: '0 4px 16px rgba(232,54,58,0.35)',
                        }}
                      >
                        開啟買家入口
                      </Button>
                      <Button
                        size="large"
                        icon={<RobotOutlined />}
                        onClick={() => navigate('/house-ai')}
                        style={{
                          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                          color: '#fff', fontWeight: 600, height: 44, borderRadius: 10,
                        }}
                      >
                        管理後台
                      </Button>
                    </Space>
                  </Col>

                  {/* Featured Properties */}
                  <Col xs={24} lg={12}>
                    {houseStats.featured_properties?.length > 0 && (
                      <Row gutter={[10, 10]}>
                        {houseStats.featured_properties.slice(0, 4).map((p) => (
                          <Col xs={12} key={p.id}>
                            <div
                              onClick={() => window.open(`/h/life-property/property/${p.id}`, '_blank')}
                              style={{
                                borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                transition: 'all 0.3s ease',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                              <div style={{
                                height: 100, position: 'relative',
                                background: p.images?.[0]
                                  ? `url(${p.images[0]}) center/cover`
                                  : 'linear-gradient(135deg, #667eea, #764ba2)',
                              }}>
                                <div style={{
                                  position: 'absolute', inset: 0,
                                  background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.6))',
                                }} />
                                <Tag style={{
                                  position: 'absolute', top: 8, left: 8,
                                  background: 'rgba(0,0,0,0.5)', border: 'none',
                                  color: '#fff', fontSize: 10, borderRadius: 6,
                                }}>
                                  {p.listing_type === 'sale' ? 'SALE' : p.listing_type === 'rent' ? 'RENT' : 'SALE/RENT'}
                                </Tag>
                              </div>
                              <div style={{ padding: '10px 12px' }}>
                                <div style={{
                                  color: '#fff', fontSize: 12, fontWeight: 600,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {p.title?.split(' - ')[0] || p.title}
                                </div>
                                <div style={{ color: '#d4a537', fontSize: 13, fontWeight: 700, marginTop: 2 }}>
                                  {p.price >= 1000000 ? `${(p.price / 1000000).toFixed(1)}M` : p.price >= 1000 ? `${(p.price / 1000).toFixed(0)}K` : p.price?.toLocaleString()} {p.price_currency}
                                </div>
                                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 2 }}>
                                  {p.district} · {p.bedrooms}BR · {p.area_sqm}sqm
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Col>
                </Row>
              </div>

              {/* Bottom feature tags */}
              <div style={{
                padding: '14px 40px',
                background: 'rgba(255,255,255,0.03)',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
              }}>
                {[
                  { icon: <RobotOutlined />, text: 'AI 自動找客源' },
                  { icon: <FireOutlined />, text: 'AI 即時回覆' },
                  { icon: <VideoCameraOutlined />, text: '虛擬導覽' },
                  { icon: <HomeOutlined />, text: '智能議價' },
                  { icon: <FileTextOutlined />, text: '合約管理' },
                  { icon: <TeamOutlined />, text: '售後關懷' },
                ].map((f, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 12px', borderRadius: 20,
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.5)', fontSize: 12,
                  }}>
                    {f.icon} {f.text}
                  </span>
                ))}
                <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
                  Life Property Bangkok · Partner Portal
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ Published Sites Section ═══ */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#262626' }}>
            <GlobalOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            已發佈網站
          </h3>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {publishedSites.length} 個公開網站
          </Text>
        </div>

        {sitesLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : publishedSites.length === 0 ? (
          <Empty description="尚無已發佈網站" />
        ) : (
          <Row gutter={[16, 16]}>
            {publishedSites.map((site) => {
              const typeConf = SITE_TYPE_CONFIG[site.type] || SITE_TYPE_CONFIG.scenario_site;
              const fullUrl = `${BASE_URL}${site.url}`;
              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={`${site.type}-${site.id}`}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: 14,
                      overflow: 'hidden',
                      border: 'none',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    styles={{ body: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column' } }}
                  >
                    {/* Color header bar */}
                    <div style={{
                      height: 6,
                      background: `linear-gradient(90deg, ${site.brand_color}, ${site.brand_color}88)`,
                    }} />

                    <div style={{ padding: '16px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Type tag */}
                      <div style={{ marginBottom: 8 }}>
                        <Tag
                          color={typeConf.color}
                          style={{ borderRadius: 10, fontSize: 11, fontWeight: 600 }}
                        >
                          {typeConf.icon} {typeConf.label}
                        </Tag>
                      </div>

                      {/* Title */}
                      <h4 style={{
                        fontSize: 15,
                        fontWeight: 600,
                        margin: '0 0 6px',
                        color: '#262626',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {site.title}
                      </h4>

                      {/* Description */}
                      {site.description && (
                        <Text type="secondary" style={{
                          fontSize: 12,
                          lineHeight: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          marginBottom: 10,
                        }}>
                          {site.description}
                        </Text>
                      )}

                      {/* Stats */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                        {site.product_count > 0 && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <ShopOutlined style={{ marginRight: 4 }} />{site.product_count} 產品
                          </Text>
                        )}
                        {site.event_count > 0 && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <CalendarOutlined style={{ marginRight: 4 }} />{site.event_count} 活動
                          </Text>
                        )}
                        {site.type === 'partner_portal' && (
                          <div style={{ width: '100%' }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              進度 {site.progress_pct || 0}%
                            </Text>
                            <Progress
                              percent={site.progress_pct || 0}
                              size="small"
                              strokeColor={site.is_complete ? '#52c41a' : '#D4A03C'}
                              style={{ margin: 0 }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Spacer */}
                      <div style={{ flex: 1 }} />

                      {/* URL display */}
                      <div style={{
                        background: '#f5f5f5',
                        borderRadius: 8,
                        padding: '6px 10px',
                        marginBottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                        <LinkOutlined style={{ color: '#8c8c8c', fontSize: 12, flexShrink: 0 }} />
                        <Text
                          style={{
                            fontSize: 11,
                            color: '#595959',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {fullUrl}
                        </Text>
                        <Tooltip title="複製連結">
                          <CopyOutlined
                            style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13, flexShrink: 0 }}
                            onClick={(e) => { e.stopPropagation(); copyUrl(site.url); }}
                          />
                        </Tooltip>
                      </div>

                      {/* Action buttons */}
                      <Space style={{ width: '100%' }}>
                        <Button
                          type="primary"
                          size="small"
                          icon={<ExportOutlined />}
                          onClick={() => window.open(site.url, '_blank')}
                          style={{
                            background: site.brand_color,
                            borderColor: site.brand_color,
                            borderRadius: 6,
                            fontWeight: 500,
                          }}
                        >
                          開啟網站
                        </Button>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyUrl(site.url)}
                          style={{ borderRadius: 6 }}
                        >
                          複製連結
                        </Button>
                      </Space>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* AI SEO 額度 */}
      <Card
        style={{ marginBottom: 24, borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
        styles={{ body: { padding: '18px 24px' } }}
      >
        <Row align="middle" gutter={16} wrap>
          <Col flex="auto">
            <Space direction="vertical" size={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ThunderboltOutlined style={{ color: '#722ed1', fontSize: 20 }} />
                <span style={{ fontWeight: 600, fontSize: 15 }}>AI SEO 使用額度</span>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                剩餘 AEO 掃描次數（來自已核發的優惠券），用於 AEO Scanner / Citation Monitor / AEO Auto-Fix
              </Text>
            </Space>
          </Col>
          <Col>
            <Space size={28} align="center" wrap>
              <Statistic
                title="剩餘 AEO 掃描"
                value={aeoStats?.remaining_scans ?? 0}
                valueStyle={{ color: (aeoStats?.remaining_scans ?? 0) > 0 ? '#52c41a' : '#ff4d4f' }}
                suffix="次"
              />
              <Statistic title="已核發優惠券" value={aeoStats?.approved_count ?? 0} suffix="張" />
              <Statistic title="待審核申請" value={aeoStats?.pending_count ?? 0} suffix="筆" />
              <Button type="primary" icon={<RocketOutlined />} onClick={() => navigate('/coupon-request')}>
                查看 / 申請優惠券
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Stats Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {statCards.map((s) => (
          <Col xs={12} sm={6} key={s.title}>
            <Card
              style={{
                borderRadius: 12,
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
              styles={{ body: { padding: '20px 24px' } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: s.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 22,
                }}>
                  {s.icon}
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: 13 }}>{s.title}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1.2 }}>
                    {s.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Quick Actions */}
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#262626' }}>
        快速操作
      </h3>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {quickActions.map((action) => (
          <Col xs={24} sm={12} md={6} key={action.title}>
            <Card
              hoverable
              onClick={() => navigate(action.path)}
              style={{
                borderRadius: 12,
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                height: '100%',
              }}
              styles={{ body: { padding: 24 } }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: action.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: action.color,
                marginBottom: 16,
              }}>
                {action.icon}
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px', color: '#262626' }}>
                {action.title}
              </h4>
              <p style={{ color: '#8c8c8c', fontSize: 13, margin: 0, lineHeight: 1.6 }}>
                {action.desc}
              </p>
              <div style={{ marginTop: 12 }}>
                <span style={{ color: action.color, fontSize: 13, fontWeight: 500 }}>
                  前往 <ArrowRightOutlined />
                </span>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Recent Responses */}
      {recentDealers.length > 0 && (
        <>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#262626' }}>
            最新回覆
          </h3>
          <Card style={{
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            {recentDealers.map((d, i) => (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: i < recentDealers.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <Space>
                  <Badge status="success" />
                  <strong>{d.company_name}</strong>
                  {d.contact_person && (
                    <span style={{ color: '#8c8c8c' }}>({d.contact_person})</span>
                  )}
                  {d.interested_brand_names && d.interested_brand_names.map((name) => (
                    <Tag color="blue" key={name}>{name}</Tag>
                  ))}
                </Space>
                <span style={{ color: '#bfbfbf', fontSize: 13 }}>
                  {d.responded_at ? new Date(d.responded_at).toLocaleString('zh-TW') : ''}
                </span>
              </div>
            ))}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button type="link" onClick={() => navigate('/loi')}>
                查看全部經銷商狀態 <ArrowRightOutlined />
              </Button>
            </div>
          </Card>
        </>
      )}

      {/* SMTP Info */}
      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: 'linear-gradient(135deg, #f6ffed, #d9f7be)',
              border: '1px solid #b7eb8f',
            }}
          >
            <Row align="middle" gutter={16}>
              <Col>
                <MailOutlined style={{ fontSize: 20, color: '#52c41a' }} />
              </Col>
              <Col flex="auto">
                <span style={{ fontWeight: 600, color: '#389e0d' }}>郵件服務已啟用</span>
                <span style={{ color: '#595959', marginLeft: 12, fontSize: 13 }}>
                  寄件者：宇聯科技 Jolie (jolie@comboware.io) &middot; SMTP: smtp.gmail.com:587
                </span>
              </Col>
              <Col>
                <Button size="small" type="link" onClick={() => navigate('/email-compose')}>
                  前往發送郵件
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default HomePage;
